import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Like useState, but buffers all functional updates and applies them together
 * in a single animation frame, reducing re-renders.
 */
export function useStateUpdateOnAnimationFrame<T>(
  initialValue: T,
): [T, (updater: (prev: T) => T) => void] {
  const [state, setState] = useState<T>(initialValue)

  const pendingUpdates = useRef<((prev: T) => T)[]>([])
  const frameRef = useRef<number | null>(null)
  const stateRef = useRef<T>(initialValue) // Local mirror of state

  const flush = () => {
    let nextState = stateRef.current
    for (const update of pendingUpdates.current) {
      nextState = update(nextState)
    }
    pendingUpdates.current = []

    if (nextState !== stateRef.current) {
      stateRef.current = nextState
      setState(nextState)
    }

    frameRef.current = null
  }

  const scheduleUpdate = useCallback((updater: (prev: T) => T) => {
    pendingUpdates.current.push(updater)

    if (frameRef.current === null) {
      frameRef.current = requestAnimationFrame(flush)
    }
  }, [])

  useEffect(() => {
    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current)
      }
    }
  }, [])

  return [state, scheduleUpdate]
}
